import {KeyTag} from "../../api/Model";
import React, {useCallback} from "react";
import {IconButton, TableCell, Tooltip} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {DataTable} from "../../components/data-table/DataTable";
import {KeyTagChip} from "../../components/key-tag-chip/KeyTagChip";
import {idFn} from "../../util/FunctionUtil";

function ActionColumn({keyTag}: { keyTag: KeyTag }) {
    const context = React.useContext(KeyTagActionContext);
    if (!context) throw new Error('KeyTagActionContext not found');
    const {
        onEditTag: editTag,
        onDeleteTag: deleteTag,
    } = context;

    const handleEdit = useCallback(() => {
        editTag(keyTag);
    }, [editTag, keyTag]);

    const handleDelete = useCallback(() => {
        deleteTag(keyTag);
    }, [deleteTag, keyTag]);

    return (
        <TableCell align="right">
            <Tooltip title="Edit">
                <IconButton
                    color="primary"
                    onClick={handleEdit}
                >
                    <EditIcon/>
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton
                    color="error"
                    onClick={handleDelete}
                >
                    <DeleteIcon/>
                </IconButton>
            </Tooltip>
        </TableCell>
    );
}

const columns: TableColumnData<KeyTag>[] = [
    {
        key: 'name',
        header: 'Name',
        cell: keyTag => <TableCell><KeyTagChip keyTag={keyTag}/></TableCell>,
    },
    {
        key: 'description',
        header: 'Description',
        cell: keyTag => <TableCell>{keyTag.description || '-'}</TableCell>,
    },
    {
        key: 'actions',
        header: 'Actions',
        width: '12em',
        align: 'right',
        cell: keyTag => <ActionColumn keyTag={keyTag}/>,
    },
];

type KeyTagActions = {
    onEditTag: (tag: KeyTag) => void;
    onDeleteTag: (tag: KeyTag) => void;
}

const KeyTagActionContext = React.createContext<KeyTagActions | undefined>(undefined);

export function KeyTagTable({rows, onEditTag, onDeleteTag}: {
    rows: KeyTag[],
    onEditTag: (tag: KeyTag) => void,
    onDeleteTag: (tag: KeyTag) => void,
}) {
    return (
        <KeyTagActionContext.Provider value={{onEditTag, onDeleteTag}}>
            <DataTable
                id={idFn}
                columns={columns}
                rows={rows}
            />
        </KeyTagActionContext.Provider>
    );
}